import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { FieldError, useFormContext } from 'react-hook-form';
import { DateInput } from '../../../../components-v2/DateInput';
import { TextArea } from '../../../../components-v2/TextArea';
import { THEME } from '../../../../components-v2/Theme/theme';
import { UnifiedImageDialog } from '../../../../components-v2/UnifiedImageDialog';
import { Badge } from '../../../../components/dataBoards/Badge';
import { DonationCampaignSearch } from '../../../../components/dataBoards/donations/DonationCampaignSearch';
import { ImagePreview } from '../../../../components/ImagePreview';
import { ImageUploadButtons } from '../../../../components/ImageUploadButtons';
import { Separator } from '../../../../components/Separator';
import { XIcon } from '../../../../components/svgs/icons/XIcon';
import { Toggle } from '../../../../components/Toggle';
import { white } from '../../../../styles/colors';
import { FormFieldWrapper } from '../../shared/components/FormFieldWrapper';
import { styleSheet } from '../styles';
import { DonationSurveyFormData } from '../utils';

export const PageSetup: React.FC<{ survey: Api.IDonationSurvey }> = ({ survey }) => {
	const isArchived = !!survey?.archivedDate;
	const {
		control,
		watch,
		setValue,
		formState: { errors },
	} = useFormContext<DonationSurveyFormData>();
	const formValues = watch();

	const [unifiedDialogOpen, setUnifiedDialogOpen] = React.useState(false);
	const [unifiedDialogTab, setUnifiedDialogTab] = React.useState<'upload' | 'free-images'>('upload');

	// Reset require when campaigns are disabled
	React.useEffect(() => {
		if (!formValues.allowProgramSpecificDonations) {
			setValue('requireCampaignSelection', false);
		}
	}, [formValues.allowProgramSpecificDonations, setValue]);

	return (
		<section className={css(styleSheet.section)}>
			<h3 className={css(styleSheet.sectionTitle)}>Page Setup</h3>
			<div className={css(styleSheet.formSection)}>
				<div className={css(styleSheet.formField)}>
					<FormFieldWrapper
						name='causeDetails'
						control={control}
						title='Cause Details'
						description='This will show up as a description for your donation page'
						error={errors.causeDetails}
						showError={Boolean(errors.causeDetails?.message)}
						controllerWrapperStyles={[styleSheet.causeDetailsTextAreaContainer]}
					>
						{({ value, onChange }) => (
							<TextArea
								styles={[styleSheet.causeDetailsTextArea]}
								disabled={isArchived}
								id={'cause-details-input'}
								placeholder=''
								value={value}
								onChange={onChange}
							/>
						)}
					</FormFieldWrapper>
				</div>

				<Separator />

				<div className={css(styleSheet.formField)}>
					<FormFieldWrapper
						name='allowProgramSpecificDonations'
						control={control}
						title='Allows Campaign Specific Donations'
						description='Search for existing campaigns or add a new campaign by typing a name and selecting "Add"'
					>
						{({ value, onChange }) => (
							<Toggle
								id='allow-program-specific-donations-toggle'
								isOn={value}
								onToggleCheckChanged={onChange}
								uncheckedColor={THEME.colors.background.dark}
							/>
						)}
					</FormFieldWrapper>

					{formValues.allowProgramSpecificDonations && (
						<div>
							<FormFieldWrapper
								name='programSpecificDonation'
								control={control}
								title=''
								description=''
								containerStyles={[styleSheet.campaignControllerSearchContainer]}
								controllerWrapperStyles={[styleSheet.campaignControllerSearchWrapper]}
								error={errors.programSpecificDonation as FieldError}
								showError={Boolean(errors.programSpecificDonation?.message)}
							>
								{({ value, onChange }) => (
									<>
										{formValues.programSpecificDonation && formValues.programSpecificDonation?.length > 0 && (
											<div className={css(styleSheet.selectedCampaignsList)}>
												{formValues.programSpecificDonation.map(campaign => (
													<div key={campaign.id} className={css(styleSheet.campaignBadgeWrapper)}>
														<Badge text={campaign.name} foregroundColor={campaign.foregroundColor || '#0066CC'} />
														{!isArchived && formValues.allowProgramSpecificDonations && (
															<button
																className={`${css(styleSheet.campaignBadgeRemove)} campaign-badge-remove`}
																onClick={() => {
																	const newCampaigns = formValues.programSpecificDonation.filter(
																		c => c.id !== campaign.id
																	);
																	setValue('programSpecificDonation', newCampaigns);
																}}
																type='button'
																aria-label={`Remove ${campaign.name}`}
															>
																<XIcon width={10} height={10} color={white} />
															</button>
														)}
													</div>
												))}
											</div>
										)}
										<DonationCampaignSearch
											initialSearchFragment=''
											onSelectDonationCampaign={(campaign: Api.IDonationCampaign) => {
												const currentCampaigns = value || [];
												if (!currentCampaigns.some((c: Api.IDonationCampaign) => c.id === campaign.id)) {
													const newCampaigns = [...currentCampaigns, campaign];
													onChange(newCampaigns);
												}
											}}
											onCreateCampaign={(campaign: Api.IDonationCampaign) => {
												const currentCampaigns = value || [];
												if (!currentCampaigns.some((c: Api.IDonationCampaign) => c.id === campaign.id)) {
													const newCampaigns = [...currentCampaigns, campaign];
													onChange(newCampaigns);
												}
											}}
											disabled={isArchived || !formValues.allowProgramSpecificDonations}
											className={css(styleSheet.campaignSearchWrapper)}
											dropdownClassName={css(styleSheet.campaignSearchDropdown)}
										/>
									</>
								)}
							</FormFieldWrapper>

							<FormFieldWrapper
								name='requireCampaignSelection'
								control={control}
								title='Require Campaign Selection'
								description='Donors must select a campaign before completing their donation'
							>
								{({ value, onChange }) => (
									<Toggle
										id='require-campaign-selection-toggle'
										isOn={value}
										onToggleCheckChanged={onChange}
										uncheckedColor={THEME.colors.background.dark}
										disabled={isArchived}
									/>
								)}
							</FormFieldWrapper>
						</div>
					)}
				</div>

				<Separator />

				<div className={css(styleSheet.formField)}>
					<FormFieldWrapper
						name='hasExpirationDate'
						control={control}
						title='Set Donation End Date'
						description='This will disable donations after the specified date'
						containerStyles={[styleSheet.dateInputHasExpirationDateContainer]}
					>
						{({ value, onChange }) => (
							<>
								<Toggle
									id='has-expiration-date-toggle'
									isOn={value}
									onToggleCheckChanged={onChange}
									uncheckedColor={THEME.colors.background.dark}
								/>
							</>
						)}
					</FormFieldWrapper>

					{formValues.hasExpirationDate && (
						<div>
							<FormFieldWrapper
								name='expirationDate'
								control={control}
								title='Expiration Date'
								description='This will disable donations after the specified date'
								error={errors.expirationDate}
								showError={Boolean(errors.expirationDate?.message)}
								controllerWrapperStyles={[styleSheet.dateInputWrapper]}
								containerStyles={[styleSheet.dateInputContainer]}
							>
								{({ value, onChange }) => <DateInput value={value} onChange={onChange} />}
							</FormFieldWrapper>
						</div>
					)}
				</div>

				<Separator />

				<div className={css(styleSheet.formField)}>
					<div className={css(styleSheet.formFieldLabelContainer)}>
						<label className={css(styleSheet.formFieldLabel)}>Upload Image</label>

						<span className={css(styleSheet.formFieldDescription)}>
							This will be displayed as the main image for your survey
						</span>
					</div>

					<div>
						{formValues.image && (
							<ImagePreview
								fileName={formValues.image?.fileName}
								fileSize={formValues.image?.fileSize}
								source={formValues.imageSource}
								onRemove={() => setValue('image', null)}
								url={formValues.image?.url}
							/>
						)}
						{!formValues.image && (
							<ImageUploadButtons
								hasImage={Boolean(formValues.image)}
								onClickFreeImage={() => {
									setUnifiedDialogTab('free-images');
									setUnifiedDialogOpen(true);
								}}
								onClickMedia={() => {
									setUnifiedDialogTab('upload');
									setUnifiedDialogOpen(true);
								}}
							/>
						)}
					</div>
				</div>

				<UnifiedImageDialog
					open={unifiedDialogOpen}
					onOpenChange={setUnifiedDialogOpen}
					initialTab={unifiedDialogTab}
					initialImage={formValues.image || undefined}
					onImageSelected={(image: Api.IFileAttachment) => {
						setValue('image', image);
					}}
				/>
			</div>
		</section>
	);
};
